import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import Layout from "@/components/layout/Layout";
import HomePage from "@/pages/HomePage";
import NotFoundPage from "@/pages/NotFoundPage";
import { LoadingScreen } from "@/components/common/LoadingSkeleton";
import SiteFavicon from "@/components/common/SiteFavicon";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/admin/ProtectedRoute";

const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContainersListPage = lazy(() => import("@/pages/ContainersListPage"));
const ContainerSlugRouter = lazy(() => import("@/pages/ContainerSlugRouter"));
const CustomisedContainersPage = lazy(() => import("@/pages/CustomisedContainersPage"));
const BlogsListPage = lazy(() => import("@/pages/BlogsListPage"));
const BlogArticlePage = lazy(() => import("@/pages/BlogArticlePage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const SitemapPage = lazy(() => import("@/pages/SitemapPage"));

// The admin panel is lazy-loaded so the CMS, its editors and the Firebase Auth
// surface stay out of the bundle visitors download.
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const AdminCategoriesPage = lazy(() => import("@/pages/admin/AdminCategoriesPage"));
const AdminProductsPage = lazy(() => import("@/pages/admin/AdminProductsPage"));
const AdminBlogsPage = lazy(() => import("@/pages/admin/AdminBlogsPage"));
const AdminSeoPage = lazy(() => import("@/pages/admin/AdminSeoPage"));
const AdminCodeInjectionPage = lazy(() => import("@/pages/admin/AdminCodeInjectionPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function App() {
    return (
        <div className="App bg-cs-offwhite text-cs-charcoal min-h-screen">
            <AuthProvider>
                {/* App-level, not inside Layout: the tab icon should be correct
                    in /admin too, unlike the analytics snippets. */}
                <SiteFavicon />
                <BrowserRouter>
                    <ScrollToTop />
                    <Suspense fallback={<LoadingScreen />}>
                        <Routes>
                            {/* Admin routes sit outside <Layout> so the marketing
                                header, footer and WhatsApp CTA never render over
                                the CMS. */}
                            <Route path="/admin/login" element={<AdminLoginPage />} />
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute>
                                        <AdminLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route index element={<AdminDashboardPage />} />
                                <Route path="categories" element={<AdminCategoriesPage />} />
                                <Route path="products" element={<AdminProductsPage />} />
                                <Route path="blogs" element={<AdminBlogsPage />} />
                                <Route path="seo" element={<AdminSeoPage />} />
                                <Route path="code-injection" element={<AdminCodeInjectionPage />} />
                                <Route path="settings" element={<AdminSettingsPage />} />
                            </Route>

                            <Route element={<Layout />}>
                                <Route path="/" element={<HomePage />} />
                                <Route path="/about-us" element={<AboutPage />} />
                                <Route path="/containers" element={<ContainersListPage />} />
                                <Route
                                    path="/containers/customised-containers"
                                    element={<CustomisedContainersPage />}
                                />
                                <Route path="/containers/:slug" element={<ContainerSlugRouter />} />
                                <Route path="/blogs" element={<BlogsListPage />} />
                                <Route path="/blogs/:slug" element={<BlogArticlePage />} />
                                <Route path="/contact-us" element={<ContactPage />} />
                                <Route path="/privacy-policy" element={<PrivacyPage />} />
                                <Route path="/terms-of-use" element={<TermsPage />} />
                                <Route path="/sitemap" element={<SitemapPage />} />
                                <Route path="*" element={<NotFoundPage />} />
                            </Route>
                        </Routes>
                    </Suspense>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
